/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';

import { SupportedLanguageCodes } from '@ph-static-data/languages';
import { notificationDefaultDuration } from '@ph-static-data/notification-durations';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';

import { Vendor } from '@ph-core/enums/vendor.enum';
import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { AuthFlowTraceService } from '@ph-core/services/auth/auth-flow-trace.service';
import { AuthService } from '@ph-core/services/auth/auth.service';
import { TokenService } from '@ph-core/services/auth/token.service';
import { ConfigService } from '@ph-core/services/config/config.service';
import { ErrorService } from '@ph-core/services/error/error.service';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { OktaService } from '@ph-core/services/okta/okta.service';
import { RequestTrackerService } from '@ph-core/services/request-tracker.service';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';
import { Environment } from '@ph-env/model/environment.model';

interface Deps {
  tokenService: TokenService;
  errorService: ErrorService;
  requestTrackerService: RequestTrackerService;
  userStore: UserStoreType;
  environment: Environment;
  authService: AuthService;
  logger: LoggerService;
  authFlowTrace: AuthFlowTraceService;
}

function sanitizePasswordResetUrl(url: string): string {
  return url.replace(/\/okta\/resetpasswordvalid\/[^/?#]+/i, '/okta/resetpasswordvalid/[redacted]');
}

function isPasswordResetApiCall(url: string): boolean {
  return /\/okta\/resetpasswordvalid\//i.test(url) || /\/okta\/resetpassword(?:\?|$)/i.test(url);
}

function logPasswordResetHttp(
  deps: Pick<Deps, 'logger' | 'authFlowTrace'>,
  event: string,
  detail: Record<string, unknown>
): void {
  deps.logger.log(LoggerContext.RequestTracking, `password-reset-http: ${event}`, detail);
  deps.logger.log(LoggerContext.Auth, `password-reset-http: ${event}`, detail);
  deps.authFlowTrace.record('password-reset', `http-interceptor:${event}`, detail);
}

const API_CALLS_TO_IGNORE_LOADING: { url: string; method?: string }[] = [
  { url: '/refreshtoken' },
  { url: '/getlienholder' },
  { url: '/getcaptcha' },
  { url: '/verifyusername' },
  { url: '/contractactivity' },
  { url: '/dealer-admin/users', method: 'PUT' },
  { url: '/dealer-admin/users', method: 'POST' },
  { url: '/account-history' },
  { url: '/emulate-dealer' },
  { url: '/getalldealers' },
];
const API_CALLS_TO_IGNORE_HIDING_ERROR: string[] = ['/refreshtoken', '/manualdecode', '/getcaptcha'];
const API_CALLS_TO_IGNORE_TOKEN: string[] = ['token', 'creditins', 'ssologin'];
const API_CALLS_WITHOUT_AUTHORIZATION: string[] = ['/getclientinfo', '/ssologin', '/getOktaUser', '/login'];
const API_ERRORS_TO_IGNORE: { url: string; status: number }[] = [
  { url: '/manualdecode', status: 330 },
  { url: '/updateStatusEContract', status: 0 }, // status: 0 to ignore all statuses
  { url: '/savePowerUpEContract', status: 0 }, // status: 0 to ignore all statuses
  { url: '/saveAncillaryEContract', status: 0 }, // status: 0 to ignore all statuses
];
const CANCELLATION_API_CALLS: string[] = ['/alert/'];

function shouldDisplayError(url: string, status: number): boolean {
  return !API_ERRORS_TO_IGNORE.some(
    (call) => url.includes(call.url) && (status === call.status || call.status === 0)
  );
}

function sendRequest<T, B>(
  auth: string,
  request: HttpRequest<T>,
  next: HttpHandlerFn,
  deps: Deps
): Observable<HttpEvent<unknown>> {
  const passwordResetCall = isPasswordResetApiCall(request.url);
  const passwordResetMeta = passwordResetCall
    ? {
        method: request.method,
        sanitizedUrl: sanitizePasswordResetUrl(request.url),
        hasAuthHeader: !!auth?.trim(),
        authHeaderPrefix: auth?.trim() ? auth.trim().slice(0, 12) : null,
        href: typeof window !== 'undefined' ? window.location.href : null,
      }
    : null;

  if (passwordResetMeta) {
    logPasswordResetHttp(deps, 'sendRequest-enter', passwordResetMeta);
  }

  let byPassLoading = false;
  API_CALLS_TO_IGNORE_LOADING.forEach((url) => {
    byPassLoading ||= request.url.includes(url.url) && (!url.method || url.method === request.method);
  });

  // Bypass loading bar when API calls are made for refresh token
  if (!byPassLoading) {
    deps.requestTrackerService.addPendingRequest(request.url);
  }

  const bypassToken = API_CALLS_TO_IGNORE_TOKEN.reduce(
    (prev, url) => prev || request.url.split(/[\/\?]+/).includes(url),
    false
  );

  let authReq = request;

  if (!bypassToken) {
    const body = request?.body as Record<string, unknown> | null | undefined;
    const customLocale =
      body && typeof body === 'object' && 'locale-to-be-removed' in body
        ? (body['locale-to-be-removed'] as string)
        : undefined;

    let langCode: SupportedLanguageCodes | '' = '';
    if (ConfigService.getLanguage()) {
      langCode = ConfigService.getLanguage();
    }

    let callToken = auth;
    if (request.headers.get('TokenType') === 'looker') {
      callToken = request.headers.get('Authorization') || '';
    }

    let headers = request.headers;
    if (callToken && callToken.trim()) {
      headers = headers.append('Authorization', callToken.trim());
    }
    headers = headers.append('Accept-Language', customLocale ? customLocale : langCode);

    let requestBody = request.body;
    if (customLocale && body && typeof body === 'object') {
      const { 'locale-to-be-removed': _, ...rest } = body;
      requestBody = rest as T;
    }

    authReq = request.clone({
      headers,
      body: requestBody,
    });
  }

  if (passwordResetMeta) {
    logPasswordResetHttp(deps, 'sendRequest-forward-to-backend', {
      ...passwordResetMeta,
      bypassToken,
      byPassLoading,
      finalHasAuthorizationHeader: authReq.headers.has('Authorization'),
    });
  }

  const startedAt = Date.now();

  return next(authReq).pipe(
    tap((response) => {
      if (passwordResetMeta && response instanceof HttpResponse) {
        logPasswordResetHttp(deps, 'sendRequest-http-response', {
          ...passwordResetMeta,
          status: response.status,
          elapsedMs: Date.now() - startedAt,
          hasBody: response.body != null,
        });
      }

      if (response instanceof HttpResponse && response.status === 200) {
        let noByPass = false;

        API_CALLS_TO_IGNORE_HIDING_ERROR.forEach((url) => {
          if (request.url.endsWith(url)) {
            noByPass = true;
          }
        });
        if (!noByPass) {
          deps.errorService.hideErrorMessage();
        }
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (passwordResetMeta) {
        logPasswordResetHttp(deps, 'sendRequest-http-error', {
          ...passwordResetMeta,
          status: err.status,
          statusText: err.statusText,
          elapsedMs: Date.now() - startedAt,
          errorMessage: err?.error?.message || err?.error?.responseDescription || err.message,
        });
      }

      // Network Error - When service is down
      if (err.status === 403) {
        deps.userStore.logout({ davinciLogout: true, oktaLogout: false });
        deps.authService.cancelAllSubscriptions();
      }

      if (err.status === 0) {
        err = new HttpErrorResponse({
          status: 100,
          statusText: 'Network Error - service may be down, please try again later.',
        });
      }

      if (shouldDisplayError(request.url, err.status)) {
        let text = err?.error?.responseDescription || err?.error?.message || err.statusText;
        let duration = notificationDefaultDuration;
        if (
          !deps.environment.features.shopping.supportManualVinDecoding &&
          deps.environment.vendorName === Vendor.GM &&
          request.url.includes('/vindecode/v2')
        ) {
          text =
            'VIN details do not exist in the system. Contact Dealer Support dealersupport@gmprotection.com or (833) 955-0051';
          duration = 15000;
        }
        deps.errorService.displayErrorMessage(err.status, text, duration);
      }

      return throwError(() => err);
    }),
    finalize(() => {
      if (passwordResetMeta) {
        logPasswordResetHttp(deps, 'sendRequest-finalize', {
          ...passwordResetMeta,
          elapsedMs: Date.now() - startedAt,
        });
      }
      deps.requestTrackerService.removePendingRequest(request.url);
    })
  );
}

export const requestInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);
  const errorService = inject(ErrorService);
  const tokenService = inject(TokenService);
  const oktaService = inject(OktaService);
  const requestTrackerService = inject(RequestTrackerService);
  const logger = inject(LoggerService);
  const authFlowTrace = inject(AuthFlowTraceService);
  const environment = configService.environment;
  const userStore = inject(UserStore);
  const deps: Deps = {
    tokenService,
    errorService,
    requestTrackerService,
    userStore,
    environment,
    authService,
    logger,
    authFlowTrace,
  };

  const passwordResetCall = isPasswordResetApiCall(request.url);
  if (passwordResetCall) {
    logPasswordResetHttp(deps, 'interceptor-enter', {
      method: request.method,
      sanitizedUrl: sanitizePasswordResetUrl(request.url),
      href: typeof window !== 'undefined' ? window.location.href : null,
    });
  }

  const doesNotRequireAuthorization = API_CALLS_WITHOUT_AUTHORIZATION.some((url) => request.url.includes(url));
  if (doesNotRequireAuthorization) {
    if (passwordResetCall) {
      logPasswordResetHttp(deps, 'interceptor-path-no-auth-required', {
        sanitizedUrl: sanitizePasswordResetUrl(request.url),
      });
    }
    return sendRequest('', request, next, deps);
  }

  let authorizationToken: 'app' | 'okta' = 'app';

  for (const url of CANCELLATION_API_CALLS) {
    authorizationToken = request.url.includes(url) ? 'okta' : 'app';
  }

  if (passwordResetCall) {
    logPasswordResetHttp(deps, 'interceptor-auth-mode', {
      sanitizedUrl: sanitizePasswordResetUrl(request.url),
      authorizationToken,
      ssoEnabled: !!environment.auth.sso,
      hasAppToken: !!tokenService.getToken(),
      hasOktaAccessToken: !!oktaService.getOktaAuth()?.getAccessToken(),
    });
  }

  if (authorizationToken === 'app') {
    const tokenValue = tokenService.getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : '';
    if (passwordResetCall) {
      logPasswordResetHttp(deps, 'interceptor-using-app-token-path', {
        sanitizedUrl: sanitizePasswordResetUrl(request.url),
        hasAppToken: !!tokenValue,
      });
    }
    return sendRequest(token, request, next, deps);
  }

  if (environment.auth.sso) {
    if (passwordResetCall) {
      logPasswordResetHttp(deps, 'interceptor-using-okta-token-path', {
        sanitizedUrl: sanitizePasswordResetUrl(request.url),
        hasOktaAccessToken: !!oktaService.getOktaAuth()?.getAccessToken(),
      });
    }
    return of(oktaService.getOktaAuth()).pipe(
      filter((oktaAuth) => !!oktaAuth?.getAccessToken()),
      switchMap((oktaAuth) => {
        const oktaToken = oktaAuth?.getAccessToken();
        if (oktaToken) {
          return sendRequest(`Bearer ${oktaToken}`, request, next, deps);
        }
        if (passwordResetCall) {
          logPasswordResetHttp(deps, 'interceptor-okta-token-missing-logout', {
            sanitizedUrl: sanitizePasswordResetUrl(request.url),
          });
        }
        userStore.logout({ davinciLogout: true, oktaLogout: false });
        authService.cancelAllSubscriptions();
        return EMPTY;
      })
    );
  }
  return sendRequest('', request, next, deps);
};